import { gql, useFragment, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Code,
  Heading,
  Tooltip,
} from '@apollo/orbit';
import _ from 'lodash';
import React from 'react';
import { Route, Switch, useRouteMatch } from 'react-router';
import { Link, useLocation } from 'react-router-dom-v5-compat';

import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { PageLayout } from 'src/components/common/pageLayout/PageLayout';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { ErrorPage } from 'src/components/errorPage/ErrorPage';
import { PermissionBlockCard } from 'src/components/permissionGuards/permissionBlockCard/PermissionBlockCard';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import {
  CheckWorkflowStatusBadge,
  CheckWorkflowStatusBadgeCheckWorkflowFragment,
} from '../checksPage/checkWorkflowsListPage/checkWorkflowItem/CheckWorkflowStatusBadge';
import {
  SummaryRerunButton,
  SummaryRerunButtonWorkflowFragment,
} from '../checksPage/summaryRerunButton/SummaryRerunButton';
import { graphRefToString } from '../hooks/useGraphRef';
import {
  checkDetailsRouteConfig,
  checkOperationsTaskRouteConfig,
  checkRouteConfig,
  checkSchemaRouteConfig,
  checkTasksRouteConfig,
  recentChecksRouteConfig,
} from '../routes';
import { NonPublicGraphArea } from '../variantLayout/NonPublicGraphArea';
import { ProtectedGraphArea } from '../variantLayout/ProtectedGraphArea';

import { CheckDetails } from './CheckDetails/CheckDetails';
import { CheckSchemas } from './CheckSchemas/CheckSchemas';
import { checkTabs } from './checkSidebar/CheckSidebar';
import { CheckTasks, taskTypeDisplayValue } from './CheckTask/CheckTasks';
import {
  taskToTaskTypeTaskFragment,
  useDefaultTaskType,
} from './useDefaultTaskType';

function CheckLayoutContent() {
  const location = useLocation();
  const {
    graphId,
    graphVariant,
    checkId,
    taskType, // Task type only applys to the tasks route
  } = useRouteParams(checkTasksRouteConfig, checkRouteConfig);
  const { query: selectedOperationId } = useRouteParams(
    checkOperationsTaskRouteConfig,
    catchAllRouteConfig,
  );

  const isSchemasTab = !!useRouteMatch(checkSchemaRouteConfig.definition);
  const isDetailsTab = !!useRouteMatch(checkDetailsRouteConfig.definition);
  const isTasksTab = !!useRouteMatch(checkTasksRouteConfig.definition);

  const { data, loading, error } = useQuery<
    GraphQLTypes.CheckLayoutQuery,
    GraphQLTypes.CheckLayoutQueryVariables
  >(
    gql`
      query CheckLayoutQuery($graphId: ID!, $checkId: ID!) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $checkId) {
            id
            gitContext {
              branch
            }
            isSandboxCheck
            introspectionEndpoint
            ...CheckWorkflowStatusBadgeCheckWorkflowFragment
            tasks {
              id
              ...taskToTaskTypeTaskFragment
            }
            baseVariant {
              id
            }
            ...SummaryRerunButtonWorkflowFragment
          }
        }
      }
      ${CheckWorkflowStatusBadgeCheckWorkflowFragment}
      ${taskToTaskTypeTaskFragment}
      ${SummaryRerunButtonWorkflowFragment}
    `,
    {
      variables: { graphId, checkId },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const workflow = data?.service?.checkWorkflow;

  const defaultTaskType = useDefaultTaskType(taskType, workflow?.tasks);

  const isOperationCheckWithOperation =
    isTasksTab && defaultTaskType === 'operations' && !!selectedOperationId;
  const selectedOperationName = useFragment<
    GraphQLTypes.IndividualAffectedQueryName,
    {}
  >({
    fragment: gql`
      fragment IndividualAffectedQueryName on AffectedQuery {
        id
        name
      }
    `,
    from: { __typename: 'AffectedQuery', id: selectedOperationId },
  });
  if (loading) {
    return <Loading />;
  } else if (error) {
    return <ErrorPage error={error} />;
  } else if (
    // Right now Service.checkWorkflow will return workflows from any graph, so lets make sure it matches the url
    workflow?.baseVariant?.id !== graphRefToString({ graphId, graphVariant })
  ) {
    return <NotFound />;
  }
  const idDisplay = workflow.id.substring(
    0,
    workflow.gitContext?.branch ? 4 : 8,
  );
  const checkTitle = workflow.isSandboxCheck
    ? workflow.introspectionEndpoint
    : workflow.gitContext?.branch;

  return (
    <DocumentTitle title={checkTitle ?? idDisplay}>
      <PageLayout
        className="bg-primary"
        title={
          <nav
            className="flex gap-2 border-b border-primary bg-primary px-8 pt-4"
            aria-label="Page level"
          >
            <div className="mr-10 w-0 flex-1">
              <Breadcrumb separatorType="chevron">
                <BreadcrumbItem>
                  <BreadcrumbLink
                    as={Link}
                    to={recentChecksRouteConfig.locationFrom({
                      location,
                      fromRouteConfig: checkRouteConfig,
                      patch: { view: 'variant' },
                    })}
                  >
                    All checks
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage={isSchemasTab}>
                  <BreadcrumbLink
                    as={Link}
                    to={checkSchemaRouteConfig.locationFrom({
                      location,
                      fromRouteConfig: checkRouteConfig,
                    })}
                  >
                    {checkTitle ?? 'Check'}
                    <Tooltip
                      className="max-w-none"
                      label={`Workflow id: ${workflow.id}`}
                    >
                      <Code>{idDisplay}</Code>
                    </Tooltip>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {isDetailsTab ? (
                  <BreadcrumbItem isCurrentPage={true}>
                    <BreadcrumbLink>
                      {isDetailsTab
                        ? 'Details'
                        : taskTypeDisplayValue(defaultTaskType)}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                ) : isTasksTab ? (
                  <BreadcrumbItem
                    isCurrentPage={!isOperationCheckWithOperation}
                  >
                    <BreadcrumbLink
                      {...(isOperationCheckWithOperation && {
                        as: Link,
                        to: checkOperationsTaskRouteConfig.locationFrom({
                          location,
                          fromRouteConfig: checkOperationsTaskRouteConfig,
                          patch: { query: RouteConfig.DELETE },
                        }),
                      })}
                    >
                      {isDetailsTab
                        ? 'Details'
                        : taskTypeDisplayValue(defaultTaskType)}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                ) : null}
                {isOperationCheckWithOperation && (
                  <BreadcrumbItem isCurrentPage={true}>
                    <BreadcrumbLink>
                      {selectedOperationName.data.name ?? selectedOperationId}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                )}
              </Breadcrumb>
              <Heading
                as="h1"
                size="3xl"
                className="mb-3 flex items-center pt-4 text-2xl text-secondary"
              >
                <div className="mr-2 truncate text-3xl text-primary">
                  {checkTitle ?? 'Check'}
                </div>{' '}
                <Tooltip
                  className="max-w-none"
                  label={`Workflow id: ${workflow.id}`}
                >
                  {idDisplay}
                </Tooltip>
                <CheckWorkflowStatusBadge
                  workflow={workflow}
                  className="ml-2"
                />
              </Heading>
              {checkTabs.map((tabName) => (
                <HeaderTab
                  key={tabName}
                  tabID={tabName}
                  {...(tabName === 'Schemas'
                    ? {
                        isActive: () => isSchemasTab,
                        to: checkSchemaRouteConfig.locationFrom({
                          location,
                          fromRouteConfig: checkRouteConfig,
                        }),
                      }
                    : tabName === 'Details'
                      ? {
                          isActive: () => isDetailsTab,
                          to: checkDetailsRouteConfig.locationFrom({
                            location,
                            fromRouteConfig: checkRouteConfig,
                          }),
                        }
                      : tabName === 'Tasks'
                        ? {
                            isActive: () => isTasksTab,
                            to: checkTasksRouteConfig.locationFrom({
                              location,
                              fromRouteConfig: checkRouteConfig,
                            }),
                          }
                        : assertUnreachable(tabName))}
                />
              ))}
            </div>

            <SummaryRerunButton
              graphId={graphId}
              workflow={workflow}
              trigger={
                <Button size="md" rightIcon={<IconChevronDown />}>
                  Rerun check
                </Button>
              }
            />
          </nav>
        }
      >
        <Switch>
          <Route
            path={checkSchemaRouteConfig.definition}
            component={CheckSchemas}
          />
          <Route
            path={checkDetailsRouteConfig.definition}
            component={CheckDetails}
          />
          <Route
            path={checkTasksRouteConfig.definition}
            component={CheckTasks}
          />
          <Route component={NotFound} />
        </Switch>
      </PageLayout>
    </DocumentTitle>
  );
}

export function CheckLayout() {
  const [accountId] = useCurrentAccountId();
  const { graphId } = useRouteParams(checkRouteConfig);

  return (
    <DocumentTitle title="Checks">
      <NonPublicGraphArea>
        <ProtectedGraphArea accountId={accountId}>
          <PermissionGuard
            graphId={graphId}
            graphPermissions={['canCheckSchemas', 'canQueryStats']}
            fallback={PermissionBlockCard}
          >
            <CheckLayoutContent />
          </PermissionGuard>
        </ProtectedGraphArea>
      </NonPublicGraphArea>
    </DocumentTitle>
  );
}
